// src/contexts/PermissionsContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import unifiedApi from '../utils/unifiedApi';

const PermissionsContext = createContext();

export const PermissionsProvider = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [permissions, setPermissions] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch permissions from the API
  const fetchPermissions = useCallback(async () => {
    if (!isAuthenticated || authLoading) {
      setPermissions(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const permissionsData = await unifiedApi.getMyPermissions();
      // null means no token was available or the permissions resource was not found
      if (!permissionsData) {
        setPermissions(null);
        return;
      }
      setPermissions(permissionsData);
    } catch (err) {
      console.error('Error fetching permissions:', err);
      setError(err.message);
      setPermissions(null);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, authLoading]);

  // Fetch permissions when authentication state changes
  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // Permission checking utilities
  const hasRole = useCallback((role) => {
    return permissions?.roles?.includes(role) || false;
  }, [permissions?.roles]);

  const hasAnyRole = useCallback((roles) => {
    if (!Array.isArray(roles)) return false;
    return roles.some(role => hasRole(role));
  }, [hasRole]);

  const canManageTeam = useCallback((teamId) => {
    if (!permissions) return false;
    if (permissions.isSuperAdmin || permissions.isTenantAdmin) return true;
    return permissions.manageableTeams?.includes(teamId) || false;
  }, [permissions]);

  const canEditTeamDetails = useCallback((teamId) => {
    if (!permissions) return false;
    if (permissions.isSuperAdmin || permissions.isTenantAdmin) return true;
    if (permissions.isSparesCoordinator) return false;
    return permissions.manageableTeams?.includes(teamId) || false;
  }, [permissions]);

  const canManageLeague = useCallback((leagueId) => {
    if (!permissions) return false;
    if (permissions.isSuperAdmin || permissions.isTenantAdmin) return true;
    return permissions.manageableLeagues?.includes(leagueId) || false;
  }, [permissions]);

  const canViewLeagueDraft = useCallback((leagueId) => {
    if (!permissions) return false;
    if (permissions.isSuperAdmin || permissions.isTenantAdmin) return true;
    return permissions.draftViewableLeagues?.includes(leagueId) || false;
  }, [permissions]);

  const canViewAnyLeagueDraft = useCallback(() => {
    if (!permissions) return false;
    return permissions.isSuperAdmin
      || permissions.isTenantAdmin
      || (permissions.draftViewableLeagues?.length > 0);
  }, [permissions]);

  const canManageAnyTeam = useCallback(() => {
    if (!permissions) return false;
    return permissions.isSuperAdmin || 
           permissions.isTenantAdmin || 
           (permissions.manageableTeams && permissions.manageableTeams.length > 0);
  }, [permissions]);

  const canManageAnyLeague = useCallback(() => {
    if (!permissions) return false;
    return permissions.isSuperAdmin || 
           permissions.isTenantAdmin || 
           (permissions.manageableLeagues && permissions.manageableLeagues.length > 0);
  }, [permissions]);

  const canAccessAdmin = useCallback(() => {
    return permissions?.canAccessAdmin || false;
  }, [permissions?.canAccessAdmin]);

  // Social events visibility — League Manager and above (excludes Team-Manager-only users).
  // Tenant-level roles (Admin, SparesCoordinator, Reporter) and any LeagueManager qualify.
  const canViewSocialEvents = useCallback(() => {
    if (!permissions) return false;
    return permissions.isSuperAdmin
      || permissions.isTenantAdmin
      || permissions.isSparesCoordinator
      || permissions.isReporter
      || (permissions.manageableLeagues && permissions.manageableLeagues.length > 0);
  }, [permissions]);

  // Social events management — League Manager and above for league-scoped, Tenant Admin+ for tenant-wide.
  // The list-page Create/Edit/Delete UI requires at least league management capability.
  const canManageSocialEvents = useCallback(() => {
    if (!permissions) return false;
    return permissions.isSuperAdmin
      || permissions.isTenantAdmin
      || (permissions.manageableLeagues && permissions.manageableLeagues.length > 0);
  }, [permissions]);

  const isSuperAdmin = useCallback(() => {
    return permissions?.isSuperAdmin || false;
  }, [permissions?.isSuperAdmin]);

  // Outstanding balances report — tenant-level roles see everyone; League Managers see their leagues' members.
  const canViewOutstandingBalances = useCallback(() => {
    if (!permissions) return false;
    return !!permissions.canViewAllOutstandingBalances
      || (permissions.manageableLeagues && permissions.manageableLeagues.length > 0);
  }, [permissions]);

  const isTenantAdmin = useCallback(() => {
    return permissions?.isTenantAdmin || false;
  }, [permissions?.isTenantAdmin]);

  const isSparesCoordinator = useCallback(() => {
    return permissions?.isSparesCoordinator || false;
  }, [permissions?.isSparesCoordinator]);

  const isReporter = useCallback(() => {
    return permissions?.isReporter || false;
  }, [permissions?.isReporter]);

  const isAdmin = useCallback(() => {
    return isSuperAdmin() || isTenantAdmin();
  }, [isSuperAdmin, isTenantAdmin]);

  // Check if user has any management capabilities
  const hasManagementAccess = useCallback(() => {
    return canAccessAdmin() || canManageAnyTeam() || canManageAnyLeague();
  }, [canAccessAdmin, canManageAnyTeam, canManageAnyLeague]);

  // Refresh permissions (useful after role changes)
  const refreshPermissions = useCallback(() => {
    return fetchPermissions();
  }, [fetchPermissions]);

  const value = {
    // Raw permissions data
    permissions,
    
    // Loading and error states
    isLoading,
    error,
    
    // Permission checking functions
    hasRole,
    hasAnyRole,
    canManageTeam,
    canEditTeamDetails,
    canManageLeague,
    canViewLeagueDraft,
    canViewAnyLeagueDraft,
    canManageAnyTeam,
    canManageAnyLeague,
    canAccessAdmin,
    canViewSocialEvents,
    canManageSocialEvents,
    canViewOutstandingBalances,
    isSuperAdmin,
    isTenantAdmin,
    isSparesCoordinator,
    isReporter,
    isAdmin,
    hasManagementAccess,
    
    // Utility functions
    refreshPermissions,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

// Export individual permission hooks for convenience
export const useHasRole = (role) => {
  const { hasRole } = usePermissions();
  return hasRole(role);
};

export const useHasAnyRole = (roles) => {
  const { hasAnyRole } = usePermissions();
  return hasAnyRole(roles);
};

export const useCanManageTeam = (teamId) => {
  const { canManageTeam } = usePermissions();
  return canManageTeam(teamId);
};

export const useCanEditTeamDetails = (teamId) => {
  const { canEditTeamDetails } = usePermissions();
  return canEditTeamDetails(teamId);
};

export const useCanManageLeague = (leagueId) => {
  const { canManageLeague } = usePermissions();
  return canManageLeague(leagueId);
};

export const useCanViewLeagueDraft = (leagueId) => {
  const { canViewLeagueDraft } = usePermissions();
  return canViewLeagueDraft(leagueId);
};

export const useIsAdmin = () => {
  const { isAdmin } = usePermissions();
  return isAdmin();
};

export const useIsSuperAdmin = () => {
  const { isSuperAdmin } = usePermissions();
  return isSuperAdmin();
};

export const useCanAccessAdmin = () => {
  const { canAccessAdmin } = usePermissions();
  return canAccessAdmin();
};

export const useIsSparesCoordinator = () => {
  const { isSparesCoordinator } = usePermissions();
  return isSparesCoordinator();
};

export const useCanViewOutstandingBalances = () => {
  const { canViewOutstandingBalances } = usePermissions();
  return canViewOutstandingBalances();
};

export const useIsReporter = () => {
  const { isReporter } = usePermissions();
  return isReporter();
};